import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Avatar from "shared/Avatar/Avatar";
import { RootState, AppDispatch } from "redux/store";
import {
  getCurrentUser,
  updateUserWithImageAPI,
} from "../../redux/reducers/UserSlice/UserSlice";
import successHandler from "../../utils/helpers/SuccessHandler";
import Details from "./Details";
import PaymentInfo from "./PaymentInfo";
import Educations from "./Educations";

const StudentAccountInfo = ({ className = "" }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size should not exceed 5MB");
        return;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please upload an image file");
        return;
      }

      setUploadingImage(true);
      const formData = new FormData();
      formData.append("user[profile_image]", file);
      
      try {
        dispatch(updateUserWithImageAPI(formData))
          .unwrap()
          .then((response) => {
            if (response) {
              dispatch(getCurrentUser());
              successHandler("Profile image updated successfully");
            }
          })
          .catch((error: any) => {
            console.error("Error updating user profile image: ", error);
          })
          .finally(() => {
            setUploadingImage(false);
          });
      } catch (error) {
        console.error("Error updating user profile image: ", error);
        setUploadingImage(false);
      }
    }
  };

  // Students only need email verification
  const isDisabled = !currentUser?.email_verified;

  return (
    <div className={`${className}`}>
      {/* Profile Image Section - Only ONE at the top */}
      <div className="mb-8 sm:mb-10">
        <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8">
          {/* Avatar with Upload */}
          <div className="relative group">
            <div className="relative rounded-full overflow-hidden">
              <Avatar
                sizeClass="w-24 h-24 sm:w-32 sm:h-32"
                hasChecked={true}
                imgUrl={currentUser?.image}
                userName={currentUser?.first_name}
              />
              
              {/* Overlay on hover */}
              {!isDisabled && (
                <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer">
                  {uploadingImage ? (
                    <div className="flex flex-col items-center">
                      <svg
                        className="animate-spin h-8 w-8 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span className="mt-2 text-xs font-medium">Uploading...</span>
                    </div>
                  ) : (
                    <>
                      <svg
                        className="w-8 h-8 sm:w-10 sm:h-10"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="mt-2 text-xs font-medium">Change Photo</span>
                    </>
                  )}
                </div>
              )}
              
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={isDisabled || uploadingImage}
                className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-not-allowed"
              />
            </div>

            {/* Verified Badge */}
            {currentUser?.email_verified && (
              <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 z-10">
                <div className="relative">
                  <svg
                    className="w-7 h-7 sm:w-8 sm:h-8 drop-shadow-md"
                    viewBox="0 0 122.88 116.87"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <polygon
                      fill="#10a64a"
                      points="61.37 8.24 80.43 0 90.88 17.79 111.15 22.32 109.15 42.85 122.88 58.43 109.2 73.87 111.15 94.55 91 99 80.43 116.87 61.51 108.62 42.45 116.87 32 99.08 11.73 94.55 13.73 74.01 0 58.43 13.68 42.99 11.73 22.32 31.88 17.87 42.45 0 61.37 8.24 61.37 8.24"
                    />
                    <path
                      fill="#fff"
                      d="M37.92,65c-6.07-6.53,3.25-16.26,10-10.1,2.38,2.17,5.84,5.34,8.24,7.49L74.66,39.66C81.1,33,91.27,42.78,84.91,49.48L61.67,77.2a7.13,7.13,0,0,1-9.9.44C47.83,73.89,42.05,68.5,37.92,65Z"
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="flex-1 min-w-0">
            <div className="space-y-2">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
                {currentUser?.first_name} {currentUser?.last_name}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                {currentUser?.email}
              </p>
              {currentUser?.phone && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {currentUser.phone}
                </p>
              )}
            </div>

            {/* Upload Info */}
            <div className="mt-4 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-300 font-medium">
                    Profile Photo Tips
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                    Use a clear photo of your face. Max size: 5MB. Formats: JPG, PNG, GIF
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="relative mb-8 sm:mb-10">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200 dark:border-neutral-700"></div>
        </div>
        <div className="relative flex justify-start">
          <span className="pr-3 bg-white dark:bg-neutral-900 text-sm font-medium text-gray-500 dark:text-gray-400">
            Student Information
          </span>
        </div>
      </div>

      {/* Student-Specific Forms - Details component should NOT show profile photo */}
      <div className="space-y-6 sm:space-y-8">
        {/* Personal Details */}
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
            Personal Details
          </h3>
          <Details hideProfilePhoto={true} />
        </div>

        {/* Payment Information */}
        {/* <div className="border-t border-gray-200 dark:border-neutral-800 pt-6 sm:pt-8">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
            Payment Information
          </h3>
          <PaymentInfo />
        </div> */}

        {/* Education History */}
        <div className="border-t border-gray-200 dark:border-neutral-800 pt-6 sm:pt-8">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
            Education History
          </h3>
          <Educations />
        </div>
      </div>

      {/* Disabled State Message */}
      {isDisabled && (
        <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/50 rounded-lg">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                Email Verification Required
              </p>
              <p className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                Please verify your email to edit your profile information.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentAccountInfo;